import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';
import Button from 'src/components/design-system/Button';
import {
  AssignMultipleButtonPermissions,
  AssignSingleButtonPermissions,
} from 'src/sections/view_invoice_budget_period/index';
import useGetNearestPeriodApi from 'src/services/useGetNearestPeriodApi';
import { hasPermission } from 'src/utils/JWT';
import { percentageCal, round } from 'src/utils/number';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../invoice_details/InvoiceDetailsContext';

const distributionList = ['Percentage', 'Amount'];

interface InvoiceBudgetPeriodToolBarProps {
  handleAddRow: VoidFunction;
  handleAutoDistributution: VoidFunction;
  distributionType: 'Percentage' | 'Amount';
  onDistributionChange: (e: DropDownListChangeEvent) => void;
}

const InvoiceBudgetPeriodToolBar: FC<InvoiceBudgetPeriodToolBarProps> = ({
  handleAddRow,
  handleAutoDistributution,
  distributionType,
  onDistributionChange,
}) => {
  const { t } = useTranslation();
  const poDetails = useContextSelector(InvoiceContext, (value) => value.poDetails);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);

  const hasDistributionPermission = useContextSelector(
    InvoiceContext,
    (value) => value.hasDistributionPermission
  );
  const setInvoiceBudgetPeriods = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceBudgetPeriods
  );
  const throttledInvoiceBudgetPeriods = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceBudgetPeriods
  );
  const mpaPermissionNumber = useContextSelector(
    InvoiceContext,
    (value) => value.mpaPermissionNumber
  );
  const singlePeriodButtonRef = useRef<HTMLButtonElement>(null);
  const multiPeriodButtonRef = useRef<HTMLButtonElement>(null);
  const addRowButtonRef = useRef<HTMLButtonElement>(null);
  const distributeEquallyButtonRef = useRef<HTMLButtonElement>(null);

  const isSinglePeriodAssign = hasPermission(AssignSingleButtonPermissions, mpaPermissionNumber);
  const isMultiplePeriodAssign = hasPermission(
    AssignMultipleButtonPermissions,
    mpaPermissionNumber
  );
  const { getNearestPoPeriod } = useGetNearestPeriodApi(invoice?.receiptId);
  const grandTotalAmt = Number(invoice?.totalCostAmt) || 0;

  const handleSinglePeriodAssign = useCallback(async () => {
    const res = await getNearestPoPeriod();
    const singlePeriodAllocation = [
      {
        glPeriodId: res.periodId,
        amount: grandTotalAmt,
        percentageAmount: 100,
        isDeletable: false,
        rowId: uuid(),
        expanded: false,
      },
    ];
    setInvoiceBudgetPeriods(singlePeriodAllocation);
  }, [getNearestPoPeriod, grandTotalAmt, setInvoiceBudgetPeriods]);

  const handleMultiPeriodAssign = useCallback(() => {
    const poTotalAmount = round(
      poDetails?.allocations?.reduce((total, item) => total + item.amount, 0) || 0,
      2
    );

    const multiPeriodAllocations =
      poDetails?.allocations
        ?.map((item, indx) => {
          const percentage = (Number(item.amount) / Number(poTotalAmount)) * 100;
          const percentageAmount = round(percentage, 2);
          const allocationAmt = percentageCal(percentage, grandTotalAmt);
          return {
            ...item,
            percentageAmount: percentageAmount,
            amount: allocationAmt,
            isDeletable: indx !== 0,
            rowId: uuid(),
            expanded: false,
          };
        })
        .sort((a, b) => a.glPeriodId - b.glPeriodId) || [];
    setInvoiceBudgetPeriods(multiPeriodAllocations);
  }, [grandTotalAmt, poDetails?.allocations, setInvoiceBudgetPeriods]);

  const isSinglePeriodSaveDisable = throttledInvoiceBudgetPeriods.length === 1;
  const isMultiPeriodSaveDisable = throttledInvoiceBudgetPeriods.length > 1;

  return (
    <div className="flex items-center justify-between w-full text-[13px]">
      {isSinglePeriodAssign && (
        <Button
          onClick={handleSinglePeriodAssign}
          ref={singlePeriodButtonRef}
          disabled={isSinglePeriodSaveDisable}
          size="md"
        >
          {t('Convert to Single Period')}
        </Button>
      )}

      {isMultiplePeriodAssign && (
        <Button
          onClick={handleMultiPeriodAssign}
          ref={multiPeriodButtonRef}
          disabled={isMultiPeriodSaveDisable}
          size="md"
        >
          {t('Convert to Multi Period Allocations')}
        </Button>
      )}

      {hasDistributionPermission && (
        <>
          <div className="flex gap-2">
            <Button onClick={handleAddRow} ref={addRowButtonRef} size="md">
              {t('Add New Row')}
            </Button>
            <Button onClick={handleAutoDistributution} ref={distributeEquallyButtonRef} size="md">
              {t('Distribute Equally')}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white">{t('Distribute By')} </span>
            <DropDownList
              id="view-drop-down-list"
              style={{ width: 150 }}
              size={'small'}
              data={distributionList}
              value={distributionType}
              onChange={onDistributionChange}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default InvoiceBudgetPeriodToolBar;
